import { Row } from "antd";
import { FormInstance } from "antd";
import { CriteriaOption } from "../../../../types/bulkEmails/CriteriaOption";
import { CriteriaRowModel } from "../../../../types/bulkEmails/CriteriaRowModel";
import { memo, useState, useCallback, useEffect } from "react";
import { CriteriaRow } from "./CriteriaRow";
import { useDependenciesResolver } from "../../hooks/useDependenciesResolver";

export const CriteriaContainer = memo(
  ({
    form,
    options,
    defaultCriteria,
    updateRequest
  }: {
    form: FormInstance;
    options: CriteriaOption[];
    defaultCriteria: Omit<CriteriaRowModel, "id">[];
    updateRequest: () => void;
  }) => {
    const [rows, setRows] = useState<CriteriaRowModel[]>(() =>
      defaultCriteria.map((x) => ({
        ...x,
        id: crypto.randomUUID()
      }))
    );

    const { isDependenciesMet, removeUnsatisfiedRows } = useDependenciesResolver(options);

    const canAdd = rows.length < options.length;

    const usedOptions = rows.filter((x) => x.optionKey);

    const selectedKeys = usedOptions.map((x) => x.optionKey).join("|");

    const availableKeys = options
      .filter((option) => isDependenciesMet(option, usedOptions))
      .map((option) => option.key)
      .join("|");

    useEffect(() => {
      updateRequest();
    }, [rows, updateRequest]);

    const updateRow = useCallback(
      (row: CriteriaRowModel) => {
        setRows((previous) => removeUnsatisfiedRows(previous.map((x) => (x.id === row.id ? row : x))));
      },
      [removeUnsatisfiedRows]
    );

    const deleteRow = useCallback(
      (id: string) => {
        setRows((previous) => removeUnsatisfiedRows(previous.filter((x) => x.id !== id)));
      },
      [removeUnsatisfiedRows]
    );

    const addRow = useCallback(() => {
      setRows((previous) => [
        ...previous,
        {
          id: crypto.randomUUID(),
          optionKey: undefined,
          value: undefined,
          removable: true
        }
      ]);
    }, []);

    return (
      <>
        {rows.map((row) => (
          <CriteriaRow
            key={row.id}
            form={form}
            row={row}
            options={options}
            selectedOption={options.find((option) => option.key === row.optionKey)!}
            selectedKeys={selectedKeys}
            availableKeys={availableKeys}
            onChange={updateRow}
            onRemove={deleteRow}
          />
        ))}

        {canAdd && (
          <Row>
            <p className="mx-2 text-md w-500 logo-cursor ant-btn-link" onClick={addRow}>
              + Add Criteria
            </p>
          </Row>
        )}
      </>
    );
  }
);
